import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ArtworkCard from '../../components/ArtworkCard/ArtworkCard';
import './ArtworkGalleryPage.css';

const ArtworkGalleryPage = () => {
    const { t } = useTranslation();
    const { username } = useParams();
    const navigate = useNavigate();

    const [artworks, setArtworks] = useState([]);
    const [artistProfile, setArtistProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [availableCategories, setAvailableCategories] = useState([]);

    // Fetch artist profile
    useEffect(() => {
        const fetchArtistProfile = async () => {
            try {
                const res = await api.get(`/profile/${username}`);
                setArtistProfile(res.data);
            } catch (err) {
                console.error('Error fetching artist profile:', err);
            }
        };

        if (username) {
            fetchArtistProfile();
        }
    }, [username]);

    const fetchArtworks = useCallback(async () => {
        try {
            setLoading(true);
            const categoryParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
            const res = await api.get(
                `/artworks/user/${username}?page=${currentPage}&limit=12${categoryParam}`
            );
            setArtworks(res.data.artworks);
            setTotalPages(res.data.totalPages);

            // Extract unique categories from all artworks (only on first load)
            if (currentPage === 1 && selectedCategory === 'all') {
                const allArtworksRes = await api.get(
                    `/artworks/user/${username}?page=1&limit=1000`
                );
                const categories = [...new Set(allArtworksRes.data.artworks.map(a => a.category))];
                setAvailableCategories(categories.sort());
            }
        } catch (err) {
            console.error('Error fetching artworks:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedCategory, username]);

    useEffect(() => {
        fetchArtworks();
    }, [fetchArtworks]);

    const filteredArtworks = artworks.filter(artwork =>
        artwork.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artwork.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleArtworkClick = (artworkId) => {
        navigate(`/artworks/${artworkId}`);
    };

    return (
        <div className="artwork-gallery-page">
            {/* Navbar removed */}
            <div className="gallery-content">
                <div className="gallery-header">
                    <div className="breadcrumb">
                        <button onClick={() => navigate(`/profile/${username}`)}>
                            ← {t('back_to_home')}
                        </button>
                    </div>

                    {/* Artist Profile Card */}
                    {artistProfile && (
                        <div className="artist-profile-card">
                            <div className="artist-avatar">
                                {artistProfile.profilePicture ? (
                                    <img src={artistProfile.profilePicture} alt={artistProfile.artistName || username} />
                                ) : (
                                    <div className="avatar-fallback">
                                        {(artistProfile.artistName || username).substring(0, 2).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            <div className="artist-info">
                                <h1>
                                    {artistProfile.artistName || username}
                                    {artistProfile.isVerified && (
                                        <svg className="verified-badge" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                                        </svg>
                                    )}
                                </h1>
                                <p className="username-display">@{username}</p>
                            </div>
                        </div>
                    )}

                    {!artistProfile && (
                        <>
                            <h1>{t('view_all_artworks')}</h1>
                            <p className="username-display">@{username}</p>
                        </>
                    )}
                </div>

                <div className="gallery-filters">
                    <input
                        type="text"
                        placeholder={t('search_artworks')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />

                    <select
                        value={selectedCategory}
                        onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="category-filter"
                    >
                        <option value="all">{t('all_categories')}</option>
                        {availableCategories.map(cat => (
                            <option key={cat} value={cat}>
                                {t(`category_${cat.toLowerCase()}`)}
                            </option>
                        ))}
                    </select>
                </div>

                {loading ? (
                    <div className="loading-state">{t('loading')}</div>
                ) : filteredArtworks.length === 0 ? (
                    <div className="empty-state">
                        <h3>{t('no_artworks')}</h3>
                        <p>{t('no_artworks_description')}</p>
                    </div>
                ) : (
                    <>
                        <div className="artworks-grid">
                            {filteredArtworks.map((artwork) => (
                                <ArtworkCard
                                    key={artwork.id}
                                    artwork={artwork}
                                    onClick={() => handleArtworkClick(artwork.id)}
                                />
                            ))}
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    {t('previous_page')}
                                </button>
                                <span>{t('page_of', { current: currentPage, total: totalPages })}</span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    {t('next_page')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ArtworkGalleryPage;
